import { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AamBackendApi } from 'src/libs/aamBackendApi';

import { DataTable, DataTableTopToolbar, Loader, Searchbox, TabItem, Tabs } from '@adeccoux/tag-ds';
import OOTableContainer from '@components/admin/Table/OOTableContainer';
import { PageWrapper } from '@components/app/PageWrapper/PageWrapper';
import { useInjection } from '@context/inversify-context-provider';
import { useT } from '@features/oneOnboarding/hooks/useT';
import {
  useFetchBranches,
  useGetClients,
  useGetJobTypes,
  useGetPurchaseOrders,
  useGetRequisitionsByTenant,
  useRegions,
  useSitesAll,
} from '@hooks/apiHooks';
import { TYPES } from '@libs/InversifyType';
import { BranchModel } from '@models/branch.model';
import { ClientModel } from '@models/client.model';
import { PurchaseOrderModel } from '@models/purchaseOrder.model';
import { JobTypeModel, RequisitionModel } from '@models/requisition.model';
import { SiteModel } from '@models/site.model';
import { Box } from '@mui/material';
import { snackbarSlice } from '@store/slices/snackbar';
import { useQueryClient } from '@tanstack/react-query';

import CreateUpdateData from './components/MasterDataSettings/CreateUpdateData';
import MasterDataImport from './components/MasterDataSettings/MasterDataImport';
import MasterDataTable from './components/MasterDataSettings/MasterDataTable';
import { MasterDataType } from './masterDataHelpers';

type Props = {};
export type SelectedRowType =
  | BranchModel
  | SiteModel
  | RequisitionModel
  | PurchaseOrderModel
  | ClientModel
  | JobTypeModel;
function getRowId(item: SelectedRowType) {
  if ('requisitionId' in item) {
    return item.requisitionId;
  }
  return item.id;
}

const MasterDataSettings = (props: Props) => {
  const { t, i18n } = useT('recruiter', 'entry');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<MasterDataType>('branch');
  const queryClient = useQueryClient();
  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SelectedRowType | null>(null);
  const { data: sites, isLoading: loadingSites } = useSitesAll();
  const { data: requisitionsRaw, isLoading: loadingRequisitions } = useGetRequisitionsByTenant(true);
  const { data: regions, isLoading: loadingRegions } = useRegions();
  const { data: clients, isLoading: loadingClients } = useGetClients(true);
  const { data: branches, isLoading: loadingBranch } = useFetchBranches(true, true);
  const { data: purchaseOrders, isLoading: loadingPurchaseOrders } = useGetPurchaseOrders(true);
  const { data: jobTypes, isLoading: loadingJobTypes } = useGetJobTypes(true);
  const importRef = useRef<{ handleImport: (file: File) => void }>(null);
  const isLoading =
    loadingBranch &&
    loadingRegions &&
    loadingSites &&
    loadingJobTypes &&
    loadingRequisitions &&
    loadingPurchaseOrders &&
    loadingClients;
  const requisitions = useMemo(() => {
    return requisitionsRaw?.map((req) => ({
      ...req,
      site: sites?.find((site) => site.id === req.siteId),
    }));
  }, [requisitionsRaw, sites]);
  function handleSelectRow(item: SelectedRowType) {
    if (!item) return;

    const itemId = getRowId(item);
    const selectedId = selectedRow ? getRowId(selectedRow) : null;

    if (selectedId !== itemId) {
      setSelectedRow(item);
    } else {
      setSelectedRow(null);
    }
  }
  const handleDelete = async () => {
    if (!selectedRow) return;
    if (window.confirm(translate('BranchRemovalConfirmation', 'Are you sure you want to remove this branch?'))) {
      switch (activeTab) {
        case 'branch': {
          try {
            await aamBackendApi.deleteBranch(getRowId(selectedRow));
            dispatch(
              snackbarSlice.actions.showSuccess({
                message: translate('BranchRemoveSuccess', 'Branch Deleted Successfully'),
              }),
            );
            queryClient.invalidateQueries({ queryKey: ['getBranches'] });
          } catch (err) {
            let msg;
            if (err.response?.data?.error?.data) {
              const { message, name, id } = err.response.data.error.data;
              if (message) {
                msg = message;
              }
              if (name && id) {
                msg = `Entity ${name} not found, id ${id}`;
              }
            }
            dispatch(
              snackbarSlice.actions.showError({
                message: translate('BranchRemoveError', 'Branch Removal Error') + ': ' + (msg || err),
              }),
            );
          }
          break;
        }
        case 'site': {
          try {
            await aamBackendApi.deleteSite(getRowId(selectedRow));
            dispatch(
              snackbarSlice.actions.showSuccess({
                message: translate('SiteRemoveSuccess', 'Site Deleted Successfully'),
              }),
            );
            queryClient.invalidateQueries({ queryKey: ['fetchSitesAll'] });
          } catch (err) {
            let msg;
            if (err.response?.data?.error?.data) {
              const { message, name, id } = err.response.data.error.data;
              if (message) {
                msg = message;
              }
              if (name && id) {
                msg = `Entity ${name} not found, id ${id}`;
              }
            }
            dispatch(
              snackbarSlice.actions.showError({
                message: translate('SiteRemoveError', 'Site Removal Error') + ': ' + (msg || err),
              }),
            );
          }
          break;
        }
        case 'requisition': {
          try {
            await aamBackendApi.deleteRequisition(getRowId(selectedRow));
            dispatch(
              snackbarSlice.actions.showSuccess({
                message: translate('RequisitionRemoveSuccess', 'Requisition Deleted Successfully'),
              }),
            );
            queryClient.invalidateQueries({ queryKey: ['getRequisitionsByTenant'] });
          } catch (err) {
            let msg;
            if (err.response?.data?.error?.data) {
              const { message, name, id } = err.response.data.error.data;
              if (message) {
                msg = message;
              }
              if (name && id) {
                msg = `Entity ${name} not found, id ${id}`;
              }
            }
            dispatch(
              snackbarSlice.actions.showError({
                message: translate('RequisitionRemoveError', 'Requisitions Removal Error') + ': ' + (msg || err),
              }),
            );
          }
          break;
        }
        case 'purchaseOrder': {
          try {
            await aamBackendApi.deletePurchaseOrder(getRowId(selectedRow));
            dispatch(
              snackbarSlice.actions.showSuccess({
                message: translate('PurchaseOrderRemoveSuccess', 'PurchaseOrder Deleted Successfully'),
              }),
            );
            queryClient.invalidateQueries({ queryKey: ['getPurchaseOrders'] });
          } catch (err) {
            let msg;
            if (err.response?.data?.error?.data) {
              const { message, name, id } = err.response.data.error.data;
              if (message) {
                msg = message;
              }
              if (name && id) {
                msg = `Entity ${name} not found, id ${id}`;
              }
            }
            dispatch(
              snackbarSlice.actions.showError({
                message: translate('PurchaseOrderRemoveError', 'PurchaseOrder Removal Error') + ': ' + (msg || err),
              }),
            );
          }
          break;
        }
        case 'client': {
          try {
            await aamBackendApi.deleteClient(getRowId(selectedRow));
            dispatch(
              snackbarSlice.actions.showSuccess({
                message: translate('ClientRemoveSuccess', 'Client Deleted Successfully'),
              }),
            );
            queryClient.invalidateQueries({ queryKey: ['getClients'] });
          } catch (err) {
            let msg;
            if (err.response?.data?.error?.data) {
              const { message, name, id } = err.response.data.error.data;
              if (message) {
                msg = message;
              }
              if (name && id) {
                msg = `Entity ${name} not found, id ${id}`;
              }
            }
            dispatch(
              snackbarSlice.actions.showError({
                message: translate('ClientRemoveError', 'Client Removal Error') + ': ' + (msg || err),
              }),
            );
          }
          break;
        }
        case 'jobType': {
          try {
            await aamBackendApi.deleteJobType(getRowId(selectedRow));
            dispatch(
              snackbarSlice.actions.showSuccess({
                message: translate('JobTypeRemoveSuccess', 'JobType Deleted Successfully'),
              }),
            );
            queryClient.invalidateQueries({ queryKey: ['getJobTypes'] });
          } catch (err) {
            let msg;
            if (err.response?.data?.error?.data) {
              const { message, name, id } = err.response.data.error.data;
              if (message) {
                msg = message;
              }
              if (name && id) {
                msg = `Entity ${name} not found, id ${id}`;
              }
            }
            dispatch(
              snackbarSlice.actions.showError({
                message: translate('JobTypeRemoveError', 'JobType Removal Error') + ': ' + (msg || err),
              }),
            );
          }
          break;
        }
      }
    }
  };

  const filteredData = (query: string) => {
    switch (activeTab) {
      case 'branch':
        if (!branches) return [];
        if (!searchTerm) return branches;
        return branches?.filter((branch) => {
          return branch.name.toLowerCase().includes(query.toLowerCase()) || branch.id === query || branch.sid === query;
        });
      case 'site':
        if (!sites) return [];
        if (!searchTerm) return sites;
        return sites?.filter((site) => {
          return site.name.toLowerCase().includes(query.toLowerCase()) || site.id === query || site.code === query;
        });
      case 'purchaseOrder':
        if (!purchaseOrders) return [];
        if (!searchTerm) return purchaseOrders;
        return purchaseOrders?.filter((po) => {
          return po.id === query || po.poNumber.toLowerCase().includes(query.toLowerCase());
        });
      case 'requisition':
        if (!requisitions) return [];
        if (!searchTerm) return requisitions;
        return requisitions?.filter((req) => {
          return req.requisitionId.toLowerCase().includes(query.toLowerCase()) || req.requisitionId === query;
        });
      case 'client':
        if (!clients) return [];
        if (!searchTerm) return clients;
        return clients?.filter((req) => {
          return (
            req.customerId.toLowerCase().includes(query.toLowerCase()) ||
            req.id === query ||
            req.name?.toLowerCase().includes(query.toLowerCase())
          );
        });
      case 'jobType':
        if (!jobTypes) return [];
        if (!searchTerm) return jobTypes;
        return jobTypes?.filter((req) => {
          return req.id === query || req.name?.toLowerCase().includes(query.toLowerCase());
        });
      default:
        return [];
    }
  };
  const branchFileExtension = 'csv';
  const translate = (name: string, label: string) => {
    const key = `recruiter:GENERAL.CONFIGURATION.${name}`;
    return i18n.exists(key) ? t(key) : label;
  };

  const triggerImport = (files: File[]) => {
    const file = files?.[0];
    setImportModalOpen(true);
    if (!file || !file.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a valid CSV file.');
      return;
    }

    if (file && importRef.current) {
      importRef.current.handleImport(file);
    }
  };
  return (
    <PageWrapper
      featureName="master-data"
      pageName="master-data"
      sideMenu
      headerConfig={{
        pageName: 'Master Data Settings',
        languagePicker: true,
      }}
    >
      {isLoading && <Loader />}
      {branches && sites && regions && requisitions && jobTypes && purchaseOrders && clients && (
        <OOTableContainer>
          <div className="tag-ds App feature-dashboard">
            <DataTable className="tag-ds ">
              <DataTableTopToolbar className="tag-ds" key="tableTopToolbar">
                <Tabs
                  onChangeTab={(e) => {
                    setActiveTab(e as MasterDataType);
                    setSelectedRow(null);
                  }}
                  defaultActiveTab={activeTab}
                >
                  <TabItem id="branch" title="Branches" />
                  <TabItem id="site" title="Sites" />
                  <TabItem id="requisition" title="Requisitions" />
                  <TabItem id="purchaseOrder" title="Purchase Orders" />
                  <TabItem id="jobType" title="Job Types" />
                  <TabItem id="client" title="Clients" />
                </Tabs>
                <Searchbox
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  small
                  value={searchTerm}
                />
                <Box flexDirection="row" display="flex" width="20%">
                  <button
                    className="button-primary"
                    style={{ marginRight: 8, width: '100%' }}
                    onClick={() => setModalOpen(true)}
                  >
                    {selectedRow ? (
                      <>
                        Edit
                        <span className="material-icons right">edit</span>
                      </>
                    ) : (
                      <>
                        Add
                        <span className="material-icons right">add</span>
                      </>
                    )}
                  </button>
                  {selectedRow && (
                    <button
                      className="button-secondary"
                      style={{ marginRight: 8, width: '100%' }}
                      onClick={handleDelete}
                    >
                      <>
                        Delete
                        <span className="material-icons right">delete</span>
                      </>
                    </button>
                  )}
                  {!selectedRow && (
                    <div style={{ width: '100%' }}>
                      <input
                        accept={`.${branchFileExtension}`}
                        style={{ display: 'none' }}
                        id="upload-button-file"
                        type="file"
                        onClick={(e) => {
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          e.currentTarget.value = null; // reset input so same file can be selected again
                        }}
                        onChange={(e: any) => triggerImport(e.target.files)}
                      />
                      <label htmlFor="upload-button-file">
                        <div className="button-primary">
                          Import
                          <span className="material-icons right">add</span>
                        </div>
                      </label>
                    </div>
                  )}
                </Box>
              </DataTableTopToolbar>
            </DataTable>
            <MasterDataTable
              activeTab={activeTab}
              dataSource={filteredData(searchTerm)}
              selectedRow={selectedRow}
              handleSelectRow={handleSelectRow}
            />
            <MasterDataImport
              acitveTab={activeTab}
              setModalOpen={setImportModalOpen}
              modalOpen={importModalOpen}
              ref={importRef}
              dependencies={{
                sites,
                clients,
                regions,
                purchaseOrders,
                requisitions,
                jobTypes,
                branches,
              }}
            />
            <CreateUpdateData
              dataSources={{ branches, sites, regions, clients, jobTypes }}
              activeTab={activeTab}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
              modalOpen={modalOpen}
              setModalOpen={setModalOpen}
            />
          </div>
        </OOTableContainer>
      )}
    </PageWrapper>
  );
};

export default MasterDataSettings;
